import { useState, useEffect } from 'react';
// @mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Radio,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// form
import { useForm } from 'react-hook-form';
// hooks
import useAuth from '../../../hooks/useAuth';
import useResponsive from '../../../hooks/useResponsive';
// components
import { FormProvider, RHFTextField, RHFRadioGroup } from '../../../components/hook-form';
import DIOText from '../../../components/DIOText';
// util
import axios from '../../../utils/axios';

// ----------------------------------------------------------------------

const REASON_OPTIONS = [
  '정규직 채용이 어려워서',
  '채용 비용을 줄이기 위해',
  '단기 업무 인력이 필요해서',
  '경력직 채용이 필요해서',
  '기타',
];

export default function AskingWhyFindCrewDialog({ isOpen, closeDialog, submit }) {
  const smUp = useResponsive('up', 'sm');
  const { user } = useAuth();
  const [isETC, setIsETC] = useState(false);
  const [canSubmit, setCanSubmit] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const defaultValues = {
    reason: '',
    reasonEtc: '',
  }
  const methods = useForm({
    defaultValues: defaultValues,
  });
  const { getValues, watch, reset, resetField } = methods;

  const resetAllState = async () => {
    setIsETC(false);
    setCanSubmit(false);
  }

  const resetStateAndCloseDialog = async () => {
    closeDialog();
    resetAllState();
    reset();
    if (REASON_OPTIONS.indexOf("기타") === -1) {
      REASON_OPTIONS.push("기타")
    }
    submit();
  }

  const watchValues = async (value, name, type) => {
    if (name === 'reason' && type === 'change') {
      if(value.reason === '기타') {
        setIsETC(true);
        resetField('reason');
        if (REASON_OPTIONS.indexOf("기타") > -1) {
          REASON_OPTIONS.pop();
        }
        return;
      } else {
        setIsETC(false);
        if (REASON_OPTIONS.indexOf("기타") === -1) {
          REASON_OPTIONS.push("기타");
        }
      }
    }
    setCanSubmit(!!value.reason || !!value.reasonEtc)
  }

  const submitForm = async () => {
    setIsSaving(true);
    const data = getValues();
    const submitData = {
      reason: isETC ? `기타: ${data.reasonEtc}` : data.reason,
    }
    try {
      const companyId = user?.employee.company[0].id;
      await axios.post(
        `/api/company/${companyId}/why-find-crew/`,
        submitData
      );
      resetAllState();
      setIsSaving(false);
      submit();
    } catch (err) {
      console.error(err);
      submit();
    }
  }

  useEffect(() => {
    const subscription = watch((value, { name, type }) => watchValues(value, name, type ));
    return () => subscription.unsubscribe();
  }, [watch]);

  return (
    <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="xs">
      <DialogTitle sx={{ mb: 1, pt: 4, px: { xs: 2.5, sm: 3.5 } }}>
        <DIOText />에서 크루를 찾는 이유가 무엇인가요?
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2.5, sm: 3.5 }}}>
        <DialogContentText sx={{ mb: 2 }}>
          가장 큰 이유 한 개만 선택해주세요.
        </DialogContentText>
        <FormProvider methods={methods}>
          <RHFRadioGroup
            name="reason"
            options={REASON_OPTIONS}
          />
          {isETC && (
            <RHFTextField
              name="reasonEtc"
              placeholder="기타 사유를 입력해주세요"
              fullWidth
              size="small"
              autoFocus
              sx={{
                background: (theme) => theme.palette.grey[200],
                border: null,
                borderRadius: 1,
                my: 0.5,
                "&.MuiTextField-root": {"&>.MuiInputBase-root": { pl: 0 }
              }}}
              InputProps={{
                startAdornment: <Radio checked={isETC} />,
                style: {fontSize: `0.875rem` }
              }}
            />
          )}
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          onClick={resetStateAndCloseDialog}
          loading={isSaving}
          size={smUp ? "medium" : "large"}
        >
          건너뛰기
        </LoadingButton>
        <LoadingButton
          variant="contained"
          onClick={submitForm}
          disabled={!canSubmit}
          loading={isSaving}
          size={smUp ? "medium" : "large"}
        >
          설문 제출하기
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
